import fetchDisclosureType from "@/api/fetchDisclosureType";
import { CATEGORY_MAPPING_UNKNOWN } from "@/shared/Disclosure/constants/DisclosureCategoryNameConstants";
import {
  GQL_ENUM_EXCHANGE_TYPE,
  GQL_EXCHANGE_TYPE
} from "@/shared/Disclosure/types/GQL/enum/ExchangeType";
import { PV_DISCLOSURE_TYPE_ITEM } from "@/types/PresenterModel/PV_DISCLOSURE_TYPE_ITEM";
import { PV_SELECT_OPTION } from "@/types/PresenterModel/PV_SELECT_OPTION";

type IsReadyType = "init" | "process" | "success" | "fail";

type CategoryMap = {
  [key in GQL_ENUM_EXCHANGE_TYPE]: Promise<Map<string, string>>;
};

type StateType = {
  isReady: IsReadyType;
  categoryMap: CategoryMap;
  categoryFilterList: Record<
    GQL_EXCHANGE_TYPE,
    Promise<PV_DISCLOSURE_TYPE_ITEM[]>
  >;
};

export function mappingCategoryToKor(
  data: PV_DISCLOSURE_TYPE_ITEM[]
): PV_SELECT_OPTION<string[]>[] {
  return [...data]
    .sort((a, b) => a.disclosureType.localeCompare(b.disclosureType))
    .map((item) => {
      return {
        longLabel: item.categoryIds.join(", "),
        shortLabel: item.disclosureType,
        resultLabel: item.disclosureType,
        value: item.categoryIds
      };
    });
}

const keys = Object.values(GQL_EXCHANGE_TYPE);

const state: StateType = {
  isReady: "init",
  categoryMap: {} as CategoryMap,
  categoryFilterList: {} as Record<
    GQL_EXCHANGE_TYPE,
    Promise<PV_DISCLOSURE_TYPE_ITEM[]>
  >
};

export async function initializeCategoryCode() {
  if (state.isReady !== "init") return;

  state.isReady = "process";
  try {
    state.categoryMap = keys.reduce((acc, cur) => {
      acc[cur] = fetchDisclosureType(cur).then((v) => {
        const resMap = new Map<string, string>();
        for (const item of v) {
          for (const code of item.categoryIds) {
            resMap.set(code, item.disclosureType);
          }
        }
        return resMap;
      });
      return acc;
    }, {} as CategoryMap);

    state.categoryFilterList = keys.reduce(
      (acc, cur) => {
        acc[cur] = fetchDisclosureType(cur).then((v) => [...v]);
        return acc;
      },
      {} as Record<GQL_EXCHANGE_TYPE, Promise<PV_DISCLOSURE_TYPE_ITEM[]>>
    );

    await Promise.all([
      ...Object.values(state.categoryMap),
      ...Object.values(state.categoryFilterList)
    ]);

    state.isReady = "success";
  } catch (err) {
    console.error("initializeCategoryCode failed:", err);
    state.isReady = "fail";
  }
}

export async function convertCategoryCodeToName(
  exchangeCode: GQL_ENUM_EXCHANGE_TYPE,
  categoryId?: string | null
) {
  if (state.isReady !== "success") return "";

  const categoryMap = await state.categoryMap[exchangeCode];
  if (!categoryMap?.size) return "";

  if (!categoryId) return CATEGORY_MAPPING_UNKNOWN;

  return categoryMap.get(categoryId) || CATEGORY_MAPPING_UNKNOWN;
}

export async function getCategoryType(exchangeCode: GQL_ENUM_EXCHANGE_TYPE) {
  if (state.isReady !== "success") return [];

  return await state.categoryFilterList[exchangeCode];
}

export async function getHTSCategoryType(exchangeCode: GQL_ENUM_EXCHANGE_TYPE) {
  if (state.isReady !== "success") return [];

  const response = await state.categoryFilterList[exchangeCode];

  return mappingCategoryToKor(response);
}

export function getInitializationState() {
  return state.isReady;
}
